/**
 * Stage 1 of chat identity resolution (FAME-448): costanza handles to user
 * ids, in one batched `POST /handles/resolve`.
 *
 * `userIdByHandle` is `undefined` until the lookup lands — in flight, or failed
 * when `isError`. Afterwards a handle absent from
 * the map has no mapping at all — an erased or offboarded user. The mapping is
 * immutable, so results cache indefinitely.
 */
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { reportChatError } from "../reportChatError"
import { useChatRuntime, useIsChatAuthenticated } from "./useChat"

export function useUserIdsByHandle(
  handles: string[],
  { report = true }: { report?: boolean } = {}
): {
  userIdByHandle: Map<string, string> | undefined
  isError: boolean
} {
  const { actions } = useChatRuntime()
  const isAuthenticated = useIsChatAuthenticated()

  const { data, error } = useQuery({
    queryKey: ["chat", "handles", handles],
    queryFn: () => actions.resolveHandles(handles),
    enabled: isAuthenticated && handles.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  })

  useEffect(() => {
    if (error && report)
      reportChatError("failed to resolve chat handles", error)
  }, [error, report])

  const userIdByHandle = useMemo(
    () => (data === undefined ? undefined : new Map(Object.entries(data))),
    [data]
  )

  return { userIdByHandle, isError: error !== null }
}
